import { useState } from "react";
import { Link, usePage } from "@inertiajs/react";
import { LayoutDashboard, Clock, Settings, School as SchoolIcon, Trophy, Sparkles, GraduationCap, Menu, X, CircleUser, DollarSign, ChevronDown, HelpCircle, ListChecks, DoorOpen, UserCog, ArrowLeft, Tv } from "lucide-react";
import CampaignPicker from "@/components/schools/CampaignPicker";
import SetupBanner from "@/components/schools/setup/SetupBanner";
import SignOutButton from "@/components/shared/SignOutButton";

/**
 * Layout for the /schools/:slug/* area — what a school admin sees day-to-day.
 * Warm cream sidebar with the school name, campaign picker and nav, and
 * the signed-in person (email preferences, sign out) at the bottom. Same
 * design language as AdminLayout, scoped to a single school.
 *
 * Slots:
 *   children:   page content
 *   school:     { name, slug, city, province, dashboard_path }
 *   membership: { role, school_admin, teacher }
 *   eyebrow:    uppercase label above the page title
 *   title:      the page h1
 *   actions:    right-aligned header buttons (wrap on narrow screens)
 *   back:       { href, label } — the page this one belongs to, shown as a
 *               small link above the title ("← Classrooms"). Use it instead
 *               of a "Back" button in `actions`.
 *   hideSetupBanner: pages that already show setup progress (the setup
 *               guide itself, the pre-launch dashboard)
 *
 * School admins see a setup progress banner above the page until the
 * campaign they're viewing launches (props.setup_banner).
 */
export default function SchoolLayout({ children, school, membership, eyebrow, title, actions, back, hideSetupBanner = false }) {
  const { auth, school_nav, setup_banner } = usePage().props;
  const dashPath = school.dashboard_path || `/schools/${school.slug}/dashboard`;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Resolve the currently-selected campaign so nav links that require
  // a campaign context (milestones, future: leaderboard, roster) can
  // target the right one. Falls back to the most recent campaign if
  // the picker hasn't set a selection yet.
  const selectedCampaign =
    school_nav?.campaigns?.find((c) => c.slug === school_nav.selected_campaign_slug) ||
    school_nav?.campaigns?.[0] ||
    null;
  // Keep the picker's choice when moving between campaign-scoped pages.
  // Omitted for the default campaign so everyday URLs stay clean.
  const campaignQuery =
    selectedCampaign && selectedCampaign.slug !== school_nav?.current_campaign_slug
      ? `?campaign=${encodeURIComponent(selectedCampaign.slug)}`
      : "";

  const sidebarContent = (
    <>
      <div className="p-5 border-b border-border">
        <SchoolSwitcher
          currentName={school.name}
          dashPath={dashPath}
          contexts={school_nav?.user_contexts || []}
          onNavigate={() => setMobileNavOpen(false)}
        />

        {school_nav && (
          <CampaignPicker
            campaigns={school_nav.campaigns}
            selected_campaign_slug={school_nav.selected_campaign_slug}
            campaigns_path={school_nav.campaigns_path}
          />
        )}
      </div>

      <nav className="p-3 space-y-1" onClick={() => setMobileNavOpen(false)}>
        {setup_banner && (
          <SchoolNavLink
            href={setup_banner.setup_path}
            icon={<ListChecks className="h-4 w-4" />}
            count={setup_banner.campaign && `${setup_banner.progress.complete}/${setup_banner.progress.total}`}
          >
            Setup guide
          </SchoolNavLink>
        )}
        <SchoolNavLink href={`${dashPath}${campaignQuery}`} icon={<LayoutDashboard className="h-4 w-4" />}>
          Dashboard
        </SchoolNavLink>
        {school_nav?.my_log_minutes_path && (
          <SchoolNavLink href={school_nav.my_log_minutes_path} icon={<Clock className="h-4 w-4" />}>
            Log minutes
          </SchoolNavLink>
        )}
        {selectedCampaign && (
          <SchoolNavLink href={`/schools/${school.slug}/students${campaignQuery}`} icon={<GraduationCap className="h-4 w-4" />}>
            Students
          </SchoolNavLink>
        )}
        {membership.school_admin && selectedCampaign && (
          <SchoolNavLink
            href={selectedCampaign.milestones_path}
            icon={<Trophy className="h-4 w-4" />}
          >
            School rewards
          </SchoolNavLink>
        )}
        {membership.school_admin && selectedCampaign && selectedCampaign.student_milestones_path && (
          <SchoolNavLink
            href={selectedCampaign.student_milestones_path}
            icon={<Sparkles className="h-4 w-4" />}
          >
            Student prizes
          </SchoolNavLink>
        )}
        {membership.school_admin && selectedCampaign && (
          <SchoolNavLink href={`/schools/${school.slug}/classrooms${campaignQuery}`} icon={<DoorOpen className="h-4 w-4" />}>
            Classrooms
          </SchoolNavLink>
        )}
        {membership.school_admin && selectedCampaign && (
          <SchoolNavLink href={`/schools/${school.slug}/transactions${campaignQuery}`} icon={<DollarSign className="h-4 w-4" />}>
            Donations
          </SchoolNavLink>
        )}
        {membership.school_admin && (
          <SchoolNavLink href={`/schools/${school.slug}/members`} icon={<UserCog className="h-4 w-4" />}>
            Members
          </SchoolNavLink>
        )}
        {membership.school_admin && (
          <SchoolNavLink href={`/schools/${school.slug}/tv_display`} icon={<Tv className="h-4 w-4" />}>
            TV display
          </SchoolNavLink>
        )}
        {membership.school_admin && (
          <SchoolNavLink href={`/schools/${school.slug}/settings${campaignQuery}`} icon={<Settings className="h-4 w-4" />}>
            Settings
          </SchoolNavLink>
        )}
        <SchoolNavLink
          href={membership.school_admin ? "/help/school-admins" : "/help/teachers"}
          icon={<HelpCircle className="h-4 w-4" />}
        >
          Help
        </SchoolNavLink>
      </nav>

      <div className="p-3 mt-auto border-t border-border space-y-2">
        {auth?.user && (
          <div className="flex items-center gap-2.5 px-2">
            <CircleUser className="h-8 w-8 text-muted-foreground/50 shrink-0" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{auth.user.display_name}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {membership.school_admin ? "School admin" : "Teacher"}
              </p>
              <Link href="/preferences" className="text-xs text-muted-foreground underline-offset-2 hover:text-primary hover:underline">
                Email preferences
              </Link>
            </div>
            <SignOutButton />
          </div>
        )}
        <a href="/" className="block text-center text-[10px] text-muted-foreground/60 hover:text-primary transition-colors py-1">
          Powered by <span className="font-display font-semibold">PagePledge</span>
        </a>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-card focus:px-4 focus:py-2 focus:shadow-lg focus:ring-2 focus:ring-ring">
        Skip to main content
      </a>

      {/* ── Mobile top bar ───────────────────────────────── */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-border bg-card/60">
        <Link
          href={dashPath}
          className="inline-flex items-center gap-2 font-display text-base font-semibold text-foreground"
        >
          <span className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-primary/10 text-primary">
            <SchoolIcon className="h-3.5 w-3.5" />
          </span>
          <span className="truncate">{school.name}</span>
        </Link>
        <button
          type="button"
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
          aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
        >
          {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* ── Mobile dropdown nav ──────────────────────────── */}
      {mobileNavOpen && (
        <div className="md:hidden border-b border-border bg-card/60">
          {sidebarContent}
        </div>
      )}

      {/* ── Desktop sticky sidebar ───────────────────────── */}
      <aside className="hidden md:flex md:flex-col md:w-64 md:sticky md:top-0 md:h-screen md:overflow-y-auto border-r border-border bg-card/60 shrink-0">
        {sidebarContent}
      </aside>

      {/* ── Main content (scrolls independently on desktop) ─ */}
      <main id="main" className="flex-1 min-w-0 md:overflow-y-auto">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
          {setup_banner && !hideSetupBanner && <SetupBanner banner={setup_banner} />}
          {(title || actions) && (
            <header className="mb-10 flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                {back && (
                  <Link href={back.href} className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary">
                    <ArrowLeft className="h-4 w-4" />
                    {back.label}
                  </Link>
                )}
                {eyebrow && (
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    {eyebrow}
                  </p>
                )}
                {title && (
                  <h1 className="font-display text-3xl md:text-4xl font-semibold text-foreground">
                    {title}
                  </h1>
                )}
              </div>
              {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
            </header>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}

function SchoolNavLink({ href, icon, children, count }) {
  const { url } = usePage();
  const path = url.split("?")[0];
  const hrefPath = href.split("?")[0];
  const active = path === hrefPath || path.startsWith(hrefPath + "/");
  const base = "flex items-center justify-between gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors";

  return (
    <Link
      href={href}
      className={`${base} ${
        active
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
      }`}
    >
      <span className="flex items-center gap-2.5">{icon}{children}</span>
      {count && <span className="text-[11px] font-normal text-muted-foreground font-tabular">{count}</span>}
    </Link>
  );
}

function SchoolSwitcher({ currentName, dashPath, contexts, onNavigate }) {
  const [open, setOpen] = useState(false);
  const hasMultiple = contexts.length > 1;

  if (!hasMultiple) {
    // Single context — just show the school name, no dropdown
    return (
      <Link
        href={dashPath}
        onClick={onNavigate}
        className="inline-flex items-center gap-2 font-display text-lg font-semibold text-foreground hover:text-primary transition-colors"
      >
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-primary/10 text-primary shrink-0">
          <SchoolIcon className="h-4 w-4" />
        </span>
        <span className="min-w-0 break-words leading-tight line-clamp-2" title={currentName}>{currentName}</span>
      </Link>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 font-display text-lg font-semibold text-foreground hover:text-primary transition-colors text-left"
      >
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-primary/10 text-primary shrink-0">
          <SchoolIcon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1 break-words leading-tight line-clamp-2" title={currentName}>{currentName}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-2 rounded-lg border border-border bg-card shadow-lg overflow-hidden z-50">
          {contexts.map((ctx, i) => (
            <a
              key={i}
              href={ctx.path}
              onClick={() => { setOpen(false); onNavigate(); }}
              className={`flex items-center justify-between px-4 py-2.5 text-sm hover:bg-secondary/60 transition-colors ${
                ctx.current ? "bg-primary/5 font-medium" : ""
              }`}
            >
              <span className="truncate">{ctx.label}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-2 shrink-0">{ctx.role}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
